import React, { useState, useEffect, useRef } from 'react';

import { Link,withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import queryString from 'query-string';
import { debounce, isEmpty } from 'lodash';
import useWindowSize from '../../../hooks/useWindowSize';

import SearchInput from '../../Global/SearchInput';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy } from '@progress/kendo-data-query';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';

import classes from './EnergyTable.module.css';

const EnergyTable = ({
    activeNavCompany,
    companyTabIndex,
    energySites,
    energySitesMeta,
    targetNavCompany,
    companies,
    setNavCompany,
    fetchNavClients,
    fetchCompanyEnergyData,
    resetNavOptions,
    fetchSingleSite,
    history,
    isActive,
    ...props
}) => {
    const [sort, setSort] = useState([]);
    const [skip, setSkip] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [take, setTake] = useState(10);
    const [pageable] = useState({ pageSizes: true });
    const [sites, setSites] = useState([]);
    const [siteColumnWidth, setSiteColumnWidth] = useState(250);

    const gridDivRef = useRef(null);
    const gridRef = useRef(null);

    const windowSize = useWindowSize();

    useEffect(() => {
        const columns = gridRef.current.props.children;
        setSiteColumnWidth(calculateMinColumnWidths(columns, gridDivRef.current.offsetWidth, 'name', 250, true));
    }, [windowSize]);

    useEffect(() => {
        setSearchTerm('');
    }, [activeNavCompany.id]);

    useEffect(() => {
        setSites(energySites);
    }, [energySites]);

    useEffect(()=>{
        if (activeNavCompany && activeNavCompany.name && isEmpty(energySites)) {
            handleSearch('');
        }
    }, [activeNavCompany.id]);

    useEffect(()=>{
        if (activeNavCompany && activeNavCompany.name && !isEmpty(energySites)) {
            handleSearch(searchTerm);
        }
    }, [searchTerm]);

    const handleSearch = async searchTerm => {
        setSearchTerm(searchTerm);

        let updatedSites = [...energySites];

        if (searchTerm) {

            updatedSites = updatedSites.filter(item => item.site.name.toLowerCase().includes(searchTerm.toLowerCase()));
        } else {
            updatedSites = [...energySites];
        }

        setSites(updatedSites);
        setSkip(0);
    };

    const handlePageChange = async event => {
        setSkip(event.page.skip);
        setTake(event.page.take);
    };

    const renderScoreCell = props => {
        return (
            <td>
                {props.dataItem.energystarScore}
            </td>
        );
    };

    const renderCarbonChangeCell = props => {
        return (
            <td>
                {(props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeCo2PerSqFt * 100).toFixed(2) + '%'}
                {
                    props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeCo2PerSqFt > 0 ?
                        <img className={classes.Icon} src="/assets/images/red_arrow_up.svg" alt="" />
                        :
                        <img className={classes.Icon} src="/assets/images/green_arrow_down.svg" alt="" />
                }
            </td>
        );
    };

    const renderCarbonCell = props => {
        return (
            <td>
                {props.dataItem.energyPrintSnapshotMetrics[0].current12moCo2PerSqFt || '-'}
            </td>
        );
    };

    const renderConsumptionChangeCell = props => {
        return (
            <td>
                {(props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeKbtusPerSqFt * 100).toFixed(2) + '%'}
                {
                    props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeKbtusPerSqFt > 0 ?
                        <img className={classes.Icon} src="/assets/images/red_arrow_up.svg" alt="" />
                        :
                        <img className={classes.Icon} src="/assets/images/green_arrow_down.svg" alt="" />
                }
            </td>
        );
    };

    const renderConsumptionCell = props => {
        return (
            <td>
                {props.dataItem.energyPrintSnapshotMetrics[0].current12moKbtusPerSqFt || '-'}
            </td>
        );
    };

    const renderCostChangeCell = props => {
        return (
            <td>
                {(props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeCostPerSqFt * 100).toFixed(2) + '%'}
                {
                    props.dataItem.energyPrintSnapshotMetrics[0].yoyPercentChangeCostPerSqFt > 0 ?
                        <img className={classes.Icon} src="/assets/images/red_arrow_up.svg" alt="" />
                        :
                        <img className={classes.Icon} src="/assets/images/green_arrow_down.svg" alt="" />
                }
            </td>
        );
    };

    const renderCostCell = props => {
        return (
            <td>
                {props.dataItem.energyPrintSnapshotMetrics[0].current12moCostPerSqFt || '-'}
            </td>
        );
    };

    const renderCurrentThrough = props => {
        return (
            <td>
                {moment(props.dataItem.energyPrintSnapshotMetrics[0].effectiveMonth).format('YYYY-MM')}
            </td>
        );
    };

    const renderNavUrl = dataItem => {
        const queryValues = queryString.parse(history.location.search);

        return `site?client=${dataItem.site.clientId}&company=${queryValues.company}&site=${dataItem.site.id}`;
    };

    const handleNav = async dataItem => {
        let path = '/v2/dashboard/site';

        const queryValues = queryString.parse(history.location.search);
        queryValues.client = dataItem.site.clientId;
        queryValues.site = dataItem.site.id;

        if (targetNavCompany.id !==dataItem.site.companyId) {
            resetNavOptions('all');
            const company = companies.find(item => item.company.id === dataItem.site.companyId);
            setNavCompany({
                name: company.company.name,
                id:   company.company.id
            });
            fetchNavClients(dataItem.site.companyId);
        } else {
            resetNavOptions('site');
        }

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${path}?${queryValuesString}`);

        await fetchSingleSite(queryValues);
    };

    const nameCell = props => {
        return (
            <td>
                <Link
                    to={renderNavUrl(props.dataItem)}
                    onClick={
                        (e) => {
                            if (!e.ctrlKey) {
                                handleNav(props.dataItem);
                            }
                        }
                    }
                >
                    {props.dataItem.site[props.field]}
                </Link>
            </td>
        );
    };

    const refreshGrid = () => {
        let updatedSites = [...energySites];

        if (searchTerm) {

            updatedSites = updatedSites.filter(item => item.site.name.toLowerCase().includes(searchTerm.toLowerCase()));
        } else {
            updatedSites = [...energySites];
        }

        setSites(updatedSites);
        setSkip(0);
    };

    if (!isActive) return null;

    return (
        <div ref={gridDivRef} className={classes.Table}>
            <div className={classes.Headder}>
                <div className={classes.SearchContainer}>
                    <SearchInput
                        className={classes.SearchInput}
                        search={debounce(setSearchTerm, 700)}
                        placeholder="Search by Site Name"
                        value={searchTerm}
                    />
                </div>
                <div
                    className={classes.RefreshContainer}
                    aria-hidden="true"
                    role="button"
                    tabIndex={0}
                    onClick={refreshGrid}
                >
                    <span className="material-icons-outlined">refresh</span>
                </div>
            </div>
            <Grid
                ref={gridRef} 
                sortable
                sort={sort}
                onSortChange={(e) => {
                    setSort(e.sort);
                }}
                className={classes.KendoGrid}
                data={orderBy(sites, sort).slice(skip, take + skip)}
                take={take}
                skip={skip}
                total={sites.length}
                onPageChange={handlePageChange}
                pageable={pageable}
                reorderable
            >
                <Column
                    className={classes.SiteColumn}
                    field="name"
                    title="Site"
                    width={siteColumnWidth}
                    cell={nameCell}
                />
                <Column
                    className={classes.ClientColumn}
                    field="site.clientName"
                    title="Client"
                    width={200}
                />
                <Column
                    field="site.buildingTypeName"
                    title="Building Type"
                    width={175}
                />
                <Column
                    field="sqFt"
                    title="Square Feet"
                    width={150}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].effectiveMonth"
                    title="Current Through"
                    width={165}
                    cell={renderCurrentThrough}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].current12moCostPerSqFt"
                    title="Cost/SF"
                    width={125}
                    cell={renderCostCell}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].yoyPercentChangeCostPerSqFt"
                    title="Cost % Change"
                    width={120}
                    cell={renderCostChangeCell}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].current12moKbtusPerSqFt"
                    title="Consumption /SF"
                    width={125}
                    cell={renderConsumptionCell}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].yoyPercentChangeKbtusPerSqFt"
                    title="Consumption % Change"
                    width={145}
                    cell={renderConsumptionChangeCell}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].current12moCo2PerSqFt"
                    title="Carbon/SF"
                    width={140}
                    cell={renderCarbonCell}
                />
                <Column
                    field="energyPrintSnapshotMetrics[0].yoyPercentChangeCo2PerSqFt"
                    title="Carbon % Change"
                    width={130}
                    cell={renderCarbonChangeCell}
                />
                <Column
                    field="energystarScore"
                    title="Energy Star Score"
                    width={130}
                    cell={renderScoreCell}
                />
            </Grid>
        </div>
    );
};

const mapStateToProps = state => ({
    activeNavCompany: state.bob.activeNavCompany,
    companyTabIndex:  state.dashboard.companyTabIndex,
    energySites:      state.dashboard.energySites,
    energySitesMeta:  state.dashboard.energySitesMeta,
    targetNavCompany: state.bob.targetNavCompany,
    companies:        state.core.companies
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EnergyTable));
